import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiOutlineSun, HiOutlineMoon } from "react-icons/hi";
import { personal } from "../data/content";
import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "../context/LanguageContext";

const NAV_IDS = ["home", "about", "skills", "experience", "education", "projects", "contact"];

export default function Navbar({ activeSection, cvHref }) {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const linkRefs = useRef({});
    const [indicator, setIndicator] = useState({ left: 0, width: 0, opacity: 0 });
    const { theme, toggleTheme } = useTheme();
    const { language, toggleLanguage, t } = useLanguage();

    const navItems = NAV_IDS.map((id) => ({ id, label: t.nav[id] }));

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const el = linkRefs.current[activeSection];
        if (el) {
            setIndicator({ left: el.offsetLeft, width: el.offsetWidth, opacity: 1 });
        }
    }, [activeSection, scrolled, language]);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [menuOpen]);

    const scrollToSection = (id) => {
        setMenuOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header className={`navbar ${scrolled ? "navbar--scrolled" : ""}`}>
            <nav className="navbar__inner">
                <a
                    href="#home"
                    className="navbar__logo"
                    onClick={(e) => {
                        e.preventDefault();
                        scrollToSection("home");
                    }}
                >
                    <span className="navbar__logo-mark">
                        <img src="/O (1).png" alt="Logo" className="navbar__logo-mark" />
                    </span>
                    <span className="navbar__logo-text">{personal.name}</span>
                </a>

                <div className="navbar__links">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            ref={(el) => (linkRefs.current[item.id] = el)}
                            className={`navbar__link ${activeSection === item.id ? "is-active" : ""}`}
                            onClick={() => scrollToSection(item.id)}
                        >
                            {item.label}
                        </button>
                    ))}
                    <span
                        className="navbar__indicator"
                        style={{ left: indicator.left, width: indicator.width, opacity: indicator.opacity }}
                    />
                </div>

                <div className="navbar__actions">
                    <button
                        type="button"
                        className="navbar__icon-btn"
                        onClick={toggleLanguage}
                        aria-label="Toggle language"
                        title={language === "en" ? "Auf Deutsch anzeigen" : "Switch to English"}
                    >
                        <AnimatePresence mode="wait" initial={false}>
                            <motion.span
                                key={language}
                                className="navbar__lang-code"
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                transition={{ duration: 0.22, ease: "easeInOut" }}
                            >
                                {t.meta.code}
                            </motion.span>
                        </AnimatePresence>
                    </button>

                    <button
                        type="button"
                        className="navbar__icon-btn"
                        onClick={toggleTheme}
                        aria-label={theme === "dark" ? t.theme.toggleToLight : t.theme.toggleToDark}
                        title={theme === "dark" ? t.theme.toggleToLight : t.theme.toggleToDark}
                    >
                        <AnimatePresence mode="wait" initial={false}>
                            <motion.span
                                key={theme}
                                className="navbar__theme-icon"
                                initial={{ opacity: 0, rotate: -60, x: 8 }}
                                animate={{ opacity: 1, rotate: 0, x: 0 }}
                                exit={{ opacity: 0, rotate: 60, x: -8 }}
                                transition={{ duration: 0.25, ease: "easeInOut" }}
                            >
                                {theme === "dark" ? <HiOutlineMoon /> : <HiOutlineSun />}
                            </motion.span>
                        </AnimatePresence>
                    </button>

                    <a className="navbar__cta" href={cvHref} target="_blank" rel="noreferrer">
                        {t.nav.resume}
                    </a>
                </div>

                <button
                    className={`navbar__burger ${menuOpen ? "is-open" : ""}`}
                    aria-label={menuOpen ? "Close menu" : "Open menu"}
                    onClick={() => setMenuOpen((v) => !v)}
                >
                    <span />
                    <span />
                    <span />
                </button>
            </nav>

            <AnimatePresence>
                {menuOpen && (
                    <>
                        <motion.div
                            className="navbar__backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setMenuOpen(false)}
                        />
                        <motion.div
                            className="navbar__drawer"
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", stiffness: 260, damping: 30 }}
                        >
                            <div className="navbar__drawer-toggles">
                                <button type="button" className="navbar__icon-btn" onClick={toggleLanguage}>
                                    {t.meta.code}
                                </button>
                                <button
                                    type="button"
                                    className="navbar__icon-btn"
                                    onClick={toggleTheme}
                                    aria-label={theme === "dark" ? t.theme.toggleToLight : t.theme.toggleToDark}
                                >
                                    {theme === "dark" ? <HiOutlineMoon /> : <HiOutlineSun />}
                                </button>
                            </div>

                            {navItems.map((item, i) => (
                                <motion.button
                                    key={item.id}
                                    className={`navbar__drawer-link ${activeSection === item.id ? "is-active" : ""}`}
                                    onClick={() => scrollToSection(item.id)}
                                    initial={{ opacity: 0, x: 24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.06 * i }}
                                >
                                    {item.label}
                                </motion.button>
                            ))}
                            <a
                                className="navbar__drawer-cta"
                                href={cvHref}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {t.nav.downloadResume}
                            </a>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </header>
    );
}
